import { LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { STATE_CLASSES } from '../../constants/classes';
import { EVENT_NAMES } from '../../constants/events';

/**
 * SP固定フッターコンポーネント
 *
 * @element kd-sp-fixed-footer
 * @description スクロール位置に応じてSP固定フッターの表示/非表示を切り替える。
 *              カートボタンが画面内に入ったらフッターを非表示にする。
 *
 * @attr {string} reference-selector - 基準となる要素のセレクタ（デフォルト: .product-form-cart-wrap）
 */
@customElement('kd-sp-fixed-footer')
export class KdSpFixedFooter extends LitElement {
  createRenderRoot() {
    return this;
  }

  /**
   * 基準となる要素のセレクタ
   * この要素が画面内に入ったらフッターを非表示にする
   */
  @property({ type: String, attribute: 'reference-selector' })
  referenceSelector = '.product-form-cart-wrap';

  /**
   * 現在の非表示状態
   */
  @state()
  private isHidden = false;

  /**
   * スクロールハンドラ（アンバインド用に保持）
   */
  private boundHandleScroll: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();

    this.boundHandleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.boundHandleScroll, { passive: true });
    window.addEventListener('resize', this.boundHandleScroll, { passive: true });

    // 初期状態を計算
    this.handleScroll();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();

    if (this.boundHandleScroll) {
      window.removeEventListener('scroll', this.boundHandleScroll);
      window.removeEventListener('resize', this.boundHandleScroll);
      this.boundHandleScroll = null;
    }
  }

  /**
   * スクロール・リサイズ時の処理
   * 基準要素の位置に応じてフッターの表示/非表示を切り替える
   */
  private handleScroll(): void {
    const shouldHide = this.calculateShouldHide();

    if (this.isHidden !== shouldHide) {
      this.isHidden = shouldHide;
      this.updateVisibility();
    }
  }

  /**
   * フッターを非表示にすべきかを計算
   * 基準要素の上端がビューポート内に入ったら非表示
   */
  private calculateShouldHide(): boolean {
    const referenceElements = document.querySelectorAll(this.referenceSelector);
    if (referenceElements.length === 0) {
      return false;
    }

    const referenceElement = referenceElements[referenceElements.length - 1];
    const rect = referenceElement.getBoundingClientRect();
    const windowHeight = window.innerHeight;

    return windowHeight > rect.top;
  }

  /**
   * 表示状態のCSSクラスを更新し、イベントを発火
   */
  private updateVisibility(): void {
    // Light DOM ホスト要素のクラス操作は正当な用途（wc/no-self-class の対象外）
    // eslint-disable-next-line wc/no-self-class
    this.classList.toggle(STATE_CLASSES.HIDDEN, this.isHidden);

    this.dispatchEvent(
      new CustomEvent(EVENT_NAMES.PRODUCT.FOOTER_VISIBILITY_CHANGED, {
        bubbles: true,
        composed: true,
        detail: { visible: !this.isHidden },
      })
    );
  }

  render() {
    return nothing;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kd-sp-fixed-footer': KdSpFixedFooter;
  }
}
